import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { useSuccessNotification } from '../../../components/SuccessNotification';
import { adminApi } from '../../../features/admin/admin.api';
import { useAuth } from '../../../features/auth/auth-context';
import { toDisplayMessage } from '../../../lib/error-message';

import styles from './AdminPortal.module.css';

export function AdminUserDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const { notify, dismiss } = useSuccessNotification();

  const user = useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => adminApi.getUser(id),
  });

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminApi.listRoles(),
    enabled: can(PERMISSIONS.ROLES_MANAGE),
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [roleReason, setRoleReason] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');

  // Seeded from the server once the account loads, so the checkboxes start
  // from what is actually assigned rather than from empty.
  useEffect(() => {
    if (user.data !== undefined) {
      setSelectedRoles(user.data.roles.map((role) => role.code));
    }
  }, [user.data]);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const assignRoles = useMutation({
    mutationFn: () => adminApi.assignUserRoles(id, selectedRoles, roleReason),
    onMutate: () => {
      dismiss();
    },
    onSuccess: async () => {
      notify('Roles updated.');
      setRoleReason('');
      await invalidate();
    },
  });

  const deactivate = useMutation({
    mutationFn: () => adminApi.deactivateUser(id, deactivateReason),
    onMutate: () => {
      dismiss();
    },
    onSuccess: async () => {
      notify('Account deactivated.');
      setDeactivateReason('');
      await invalidate();
    },
  });

  if (user.isPending) {
    return <PageSpinner label="Loading the account" />;
  }

  if (user.isError) {
    return <Alert tone="error">{toDisplayMessage(user.error)}</Alert>;
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{user.data.email}</h1>
        <StatusBadge status={user.data.status} />
      </header>

      <section className={styles.card} aria-labelledby="account-heading">
        <h2 className={styles.cardTitle} id="account-heading">
          Account
        </h2>

        <dl className={styles.definitionList}>
          <dt>Type</dt>
          <dd>{user.data.userType}</dd>

          <dt>Email verified</dt>
          <dd>
            {user.data.emailVerifiedAt === null
              ? 'Not verified'
              : new Date(user.data.emailVerifiedAt).toLocaleString()}
          </dd>

          <dt>Last sign-in</dt>
          <dd>
            {user.data.lastLoginAt === null
              ? 'Never'
              : new Date(user.data.lastLoginAt).toLocaleString()}
          </dd>

          <dt>Failed attempts</dt>
          <dd>{user.data.failedLoginAttempts}</dd>

          <dt>Locked until</dt>
          <dd>
            {user.data.lockedUntil === null
              ? 'Not locked'
              : new Date(user.data.lockedUntil).toLocaleString()}
          </dd>

          <dt>Created</dt>
          <dd>{new Date(user.data.createdAt).toLocaleString()}</dd>
        </dl>
      </section>

      {can(PERMISSIONS.ROLES_MANAGE) && (
        <section className={styles.card} aria-labelledby="roles-heading">
          <h2 className={styles.cardTitle} id="roles-heading">
            Roles
          </h2>

          {assignRoles.isError && <Alert tone="error">{toDisplayMessage(assignRoles.error)}</Alert>}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              assignRoles.mutate();
            }}
          >
            <fieldset className={styles.checkboxGrid}>
              <legend className={styles.legend}>Assigned roles</legend>

              {roles.data?.items.map((role) => (
                <label className={styles.checkboxRow} key={role.id}>
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.code)}
                    onChange={(event) => {
                      setSelectedRoles((current) =>
                        event.target.checked
                          ? [...current, role.code]
                          : current.filter((code) => code !== role.code),
                      );
                    }}
                  />
                  <span>
                    {role.name} <code>{role.code}</code>
                  </span>
                </label>
              ))}
            </fieldset>

            <FormField
              label="Reason for this change"
              name="roleReason"
              value={roleReason}
              onChange={(event) => {
                setRoleReason(event.target.value);
              }}
              hint="Recorded in the audit trail alongside the change."
              required
            />

            <div className={styles.actions}>
              <Button type="submit" busy={assignRoles.isPending}>
                Save roles
              </Button>
            </div>
          </form>
        </section>
      )}

      {can(PERMISSIONS.USERS_DEACTIVATE) && user.data.status !== 'DEACTIVATED' && (
        <section className={styles.card} aria-labelledby="deactivate-heading">
          <h2 className={styles.cardTitle} id="deactivate-heading">
            Deactivate
          </h2>
          <p className={styles.pageSubtitle}>
            Signs the account out everywhere and prevents further sign-in. Refused if it would
            remove the last active super administrator.
          </p>

          {deactivate.isError && <Alert tone="error">{toDisplayMessage(deactivate.error)}</Alert>}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              deactivate.mutate();
            }}
          >
            <FormField
              label="Reason for deactivation"
              name="deactivateReason"
              value={deactivateReason}
              onChange={(event) => {
                setDeactivateReason(event.target.value);
              }}
              required
            />

            <div className={styles.actions}>
              <Button type="submit" variant="danger" busy={deactivate.isPending}>
                Deactivate account
              </Button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
